import React, { useCallback, useRef, useState } from 'react';
import { ID, l } from 'app/blocks/common/codes';

import './skip-link.scss';

type SkipLinkProps = {
    mainContentSelector: string;
};

const hiddenTop = -1000;

const getFirstFocusableElement = (container: Element): Element | null => {
    const focusableElements = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length > 0) {
        return focusableElements[0];
    }
    return null;
};

const SkipLink: React.FC<SkipLinkProps> = ({ mainContentSelector }) => {
    const [top, setTop] = useState(hiddenTop);
    const hasFocusedOnMount = useRef(false);

    const handleClick = useCallback(() => {
        const mainContent = document.querySelector(mainContentSelector);
        if (mainContent) {
            window.scrollTo(0, mainContent.getBoundingClientRect()?.y);
            const firstToFocus = getFirstFocusableElement(mainContent);
            if (firstToFocus) {
                (firstToFocus as HTMLElement).focus?.({ preventScroll: false });
            } else {
                if (!mainContent.hasAttribute('tabindex')) {
                    mainContent.setAttribute('tabindex', '-1');
                    mainContent.addEventListener('blur', function onBlur() {
                        mainContent.removeAttribute('tabindex');
                        mainContent.removeEventListener('blur', onBlur);
                    });
                }
                (mainContent as HTMLElement).focus?.({ preventScroll: false });
            }
        }
    }, [mainContentSelector]);

    const handleButtonFocus = useCallback(() => {
        setTop(0);
    }, [setTop]);

    const handleButtonBlur = useCallback(() => {
        setTop(hiddenTop);
    }, [setTop]);

    const handleRef = useCallback((ref: HTMLElement | null) => {
        const anchorFlag = document.getElementById('anchor_nav_flag');
        if (!ref || anchorFlag?.dataset.value === 'true') {
            if (anchorFlag) {
                anchorFlag.dataset.value = 'false';
            }
            return;
        }
        if (hasFocusedOnMount.current) {
            return;
        }
        hasFocusedOnMount.current = true;
        requestAnimationFrame(() => {
            try {
                (ref as HTMLElement).focus?.({ preventScroll: true } as FocusOptions);
            } catch (e) {
                (ref as HTMLElement).focus?.();
            }
        });
    }, []);

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <nav ref={handleRef} aria-label="Skip to Main Content" className="skip-link" tabIndex={0}>
            <button
                onBlur={handleButtonBlur}
                onClick={handleClick}
                onFocus={handleButtonFocus}
                style={{ top }}
                tabIndex={0}
                type="button"
            >
                {l(`${ID.BUTTONS}.TOP_MENU.SKIP_TO_MAIN_CONTENT`)}
            </button>
        </nav>
    );
};

export default SkipLink;
